import React, { useCallback } from 'react';
import { type UserFeaturesTypeEnum } from '@mentimeter/http-clients';
import { useRef, type ReactNode } from 'react';
import { type AnyEntitlement } from '@mentimeter/entitlements/types';
import { useOnce } from '@mentimeter/react-hooks';
import { useFeatureAccess } from '../hooks/use-feature-access';
import { CaptureInteraction } from '../triggers/CaptureInteraction';
import { UpgradeType, type NoAccess } from '../../types';

export type PaywallTriggerHandler = ({
  triggerRef,
  upgradeType,
}: {
  triggerRef: React.RefObject<HTMLElement>;
  upgradeType: 'Self upgrade' | 'Request upgrade';
}) => void;

interface PaywallTriggerProps {
  enabled: boolean;
  requiredPlan: UserFeaturesTypeEnum;

  onTrigger: PaywallTriggerHandler;
  children: ReactNode;
}

const ACCESS_LEVEL_TO_UPGRADE_TYPE: Record<NoAccess, UpgradeType> = {
  'not-in-plan': UpgradeType.SELF_UPGRADE,
  'not-in-role': UpgradeType.REQUEST_UPGRADE,
};

export function useUpgradeType(
  requiredPlan: UserFeaturesTypeEnum,
): UpgradeType | undefined {
  const accessLevel = useFeatureAccess(requiredPlan);
  const hasAccess = accessLevel === 'has-access';
  if (hasAccess) return;

  return ACCESS_LEVEL_TO_UPGRADE_TYPE[accessLevel];
}

export function PaywallTrigger({
  enabled,
  onTrigger,
  requiredPlan,
  children,
}: PaywallTriggerProps) {
  const triggerRef = useRef<HTMLDivElement>(null);
  const accessLevel = useFeatureAccess(requiredPlan);
  const hasAccess = accessLevel === 'has-access';

  const handleOpenPaywall = useCallback(() => {
    if (hasAccess) return;

    const upgradeType = ACCESS_LEVEL_TO_UPGRADE_TYPE[accessLevel];

    onTrigger({ upgradeType, triggerRef });
  }, [accessLevel, hasAccess, onTrigger]);

  if (!enabled || hasAccess) {
    return children;
  }

  return (
    <CaptureInteraction ref={triggerRef} trigger={handleOpenPaywall}>
      {children}
    </CaptureInteraction>
  );
}

interface EntitlementPaywallTriggerProps {
  entitlement: AnyEntitlement;
  onTrigger: PaywallTriggerHandler;
  children: ReactNode;
  triggerInitially?: boolean;
}

export function EntitlementPaywallTrigger({
  entitlement,
  onTrigger,
  children,
  triggerInitially = false,
}: EntitlementPaywallTriggerProps) {
  const triggerRef = useRef<HTMLDivElement>(null);

  const handleOpenPaywall = useCallback(() => {
    if (!entitlement.reason) return;

    const upgradeType = ACCESS_LEVEL_TO_UPGRADE_TYPE[entitlement.reason];

    onTrigger({ upgradeType, triggerRef });
  }, [entitlement.reason, onTrigger]);

  useOnce(triggerInitially, handleOpenPaywall);

  if (!entitlement.reason) {
    return children;
  }

  return (
    <CaptureInteraction ref={triggerRef} trigger={handleOpenPaywall}>
      {children}
    </CaptureInteraction>
  );
}
